import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { getAllEductionTopics, getEducationalPosts } from "../../api";
import useGoToPricing from "../../hooks/useGoToPricing";
import { getPublishers } from "../../flux/publisherAction";
import publisherStore from "../../flux/publisherStore";
import { isLoggedIn } from "../../utils/isLoggedIn";
import { getPublisherAvatarUrl } from "../../utils";
import { scrollPageToTop } from "../../utils/scrollPageToTop";
import Header from "../Homepage/sections/Header";
import Footer from "../Homepage/sections/Footer";
import { HomepageGlobalStyles } from "../Homepage/styles";
import MembersDateFilter from "../Members/MembersDateFilter";
import MembersPagination from "../Members/MembersPagination";
import EducationFeedSkeleton from "./EducationFeedSkeleton";
import EducationLessonCard from "./EducationLessonCard";
import {
  EDUCATION_PAGE_SIZE,
  buildEducationFilter,
} from "./educationUtils";
import {
  EducationAllLabel,
  EducationAnalystButton,
  EducationAnalystFilters,
  EducationClearButton,
  EducationContentInner,
  EducationEmptyState,
  EducationFeedSection,
  EducationFilterActions,
  EducationFilterCard,
  EducationFilterRow,
  EducationFiltersSection,
  EducationGrid,
  EducationHero,
  EducationHeroBgGradient,
  EducationHeroBgRadial,
  EducationHeroInner,
  EducationHeroTitle,
  EducationPage,
  EducationPaginationWrap,
  EducationSearchIcon,
  EducationSearchInput,
  EducationSearchWrap,
  EducationTopicChip,
  EducationTopicFilters,
} from "./Education.styles";

const Education = () => {
  const location = useLocation();
  const goToPricing = useGoToPricing();
  const loggedIn = isLoggedIn();

  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [selectedPublisherId, setSelectedPublisherId] = useState(null);
  const [query, setQuery] = useState("");
  const [publishers, setPublishers] = useState(
    publisherStore.getPublishers() || []
  );
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isClearDates, setIsClearDates] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchTimerRef = useRef(null);

  const visiblePublishers = useMemo(
    () => (publishers || []).filter((publisher) => publisher.isHidden === false),
    [publishers]
  );

  const allTopicIds = useMemo(
    () => (topics || []).map((topic) => topic.id),
    [topics]
  );

  const hasActiveFilters =
    Boolean(query.trim()) ||
    selectedTopicId != null ||
    selectedPublisherId != null ||
    Boolean(startDate) ||
    Boolean(endDate);

  const topicIdsForRequest = (topicId) =>
    topicId == null ? allTopicIds : [topicId];

  const authorIdsForRequest = (publisherId) =>
    publisherId == null ? [] : [publisherId];

  const totalPages = Math.max(1, Math.ceil(total / EDUCATION_PAGE_SIZE));

  const fetchPosts = useCallback(
    async (pageIndex, topicIds, authorIds, fromDate, toDate, searchValue) => {
      setLoading(true);
      try {
        const filter = buildEducationFilter({
          educationTopicIds: topicIds,
          authorIds,
          startDate: fromDate,
          endDate: toDate,
          searchString: searchValue,
        });
        const response = await getEducationalPosts(
          filter,
          EDUCATION_PAGE_SIZE,
          pageIndex
        );
        setPosts(response?.data?.items || []);
        setTotal(response?.data?.total || 0);
        setActivePage(pageIndex + 1);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    const onPublisherStoreChange = () => {
      setPublishers(publisherStore.getPublishers() || []);
    };

    publisherStore.addChangeListener(onPublisherStoreChange);
    if (publisherStore.getPublishers().length === 0) {
      getPublishers();
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const topicsResponse = await getAllEductionTopics();
        const loadedTopics = topicsResponse?.data || [];
        const topicIds = loadedTopics.map((topic) => topic.id);

        let initialAuthorId = null;
        if (location.state?.analyst) {
          initialAuthorId = location.state.analyst;
        }

        if (cancelled) return;

        setTopics(loadedTopics);
        setSelectedTopicId(null);
        setSelectedPublisherId(initialAuthorId);

        await fetchPosts(
          0,
          topicIds,
          initialAuthorId == null ? [] : [initialAuthorId],
          null,
          null,
          ""
        );
      } catch {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      publisherStore.removeChangeListener(onPublisherStoreChange);
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
    // Initial load only — matches legacy Education mount behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectAllTopics = () => {
    setSelectedTopicId(null);
    fetchPosts(
      0,
      allTopicIds,
      authorIdsForRequest(selectedPublisherId),
      startDate,
      endDate,
      query.trim()
    );
  };

  const selectTopic = (topicId) => {
    setSelectedTopicId(topicId);
    fetchPosts(
      0,
      [topicId],
      authorIdsForRequest(selectedPublisherId),
      startDate,
      endDate,
      query.trim()
    );
  };

  const selectPublisher = (publisherId) => {
    setSelectedPublisherId(publisherId);
    fetchPosts(
      0,
      topicIdsForRequest(selectedTopicId),
      authorIdsForRequest(publisherId),
      startDate,
      endDate,
      query.trim()
    );
  };

  const handleDatesSelected = (range) => {
    const [from, to] = range == null ? [null, null] : range;
    const isCleared = from == null && to == null;
    const isRangeComplete = from != null && to != null;

    if (!isCleared && !isRangeComplete) {
      return;
    }

    const nextStart = from != null ? dayjs(from).format("YYYY-MM-DD") : null;
    const nextEnd = to != null ? dayjs(to).format("YYYY-MM-DD") : null;

    setStartDate(nextStart);
    setEndDate(nextEnd);
    fetchPosts(
      0,
      topicIdsForRequest(selectedTopicId),
      authorIdsForRequest(selectedPublisherId),
      nextStart,
      nextEnd,
      query.trim()
    );
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      fetchPosts(
        0,
        topicIdsForRequest(selectedTopicId),
        authorIdsForRequest(selectedPublisherId),
        startDate,
        endDate,
        value.trim()
      );
    }, 400);
  };

  const clearAllFilters = () => {
    setSelectedTopicId(null);
    setSelectedPublisherId(null);
    setQuery("");
    setStartDate(null);
    setEndDate(null);
    setIsClearDates(true);
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    fetchPosts(0, allTopicIds, [], null, null, "");
    setTimeout(() => setIsClearDates(false), 0);
  };

  const handlePageChange = (pageNumber) => {
    fetchPosts(
      pageNumber - 1,
      topicIdsForRequest(selectedTopicId),
      authorIdsForRequest(selectedPublisherId),
      startDate,
      endDate,
      query.trim()
    );
    scrollPageToTop();
  };

  const allTopicsSelected = selectedTopicId == null;

  return (
    <EducationPage>
      <HomepageGlobalStyles />
      <Header onSubscribeClick={goToPricing} hideSpacer />

      <EducationHero>
        <EducationHeroBgGradient aria-hidden="true" />
        <EducationHeroBgRadial aria-hidden="true" />
        <EducationHeroInner>
          <EducationHeroTitle>
            Learn to Trade Crypto <span>Like a Professional</span>
          </EducationHeroTitle>
        </EducationHeroInner>
      </EducationHero>

      <EducationFiltersSection>
        <EducationContentInner>
          <EducationFilterCard>
            <EducationSearchWrap>
              <EducationSearchIcon className="material-icons" aria-hidden="true">
                search
              </EducationSearchIcon>
              <EducationSearchInput
                type="search"
                value={query}
                onChange={handleSearchChange}
                placeholder="Search lessons, topics, or analysts…"
                aria-label="Search lessons, topics, or analysts"
              />
            </EducationSearchWrap>

            <EducationTopicFilters>
              <EducationTopicChip
                type="button"
                $selected={allTopicsSelected}
                aria-pressed={allTopicsSelected}
                onClick={selectAllTopics}
              >
                All
              </EducationTopicChip>
              {topics.map((topic) => {
                const selected = selectedTopicId === topic.id;
                return (
                  <EducationTopicChip
                    key={topic.id}
                    type="button"
                    $selected={selected}
                    aria-pressed={selected}
                    onClick={() => selectTopic(topic.id)}
                  >
                    {topic.name}
                  </EducationTopicChip>
                );
              })}
            </EducationTopicFilters>

            <EducationFilterRow>
              <EducationAnalystFilters>
                <EducationAnalystButton
                  type="button"
                  $isAll
                  $selected={selectedPublisherId == null}
                  aria-pressed={selectedPublisherId == null}
                  title="All analysts"
                  onClick={() => selectPublisher(null)}
                >
                  <EducationAllLabel>All</EducationAllLabel>
                </EducationAnalystButton>
                {visiblePublishers.map((publisher) => {
                  const selected = selectedPublisherId === publisher.id;
                  return (
                    <EducationAnalystButton
                      key={publisher.id}
                      type="button"
                      $selected={selected}
                      aria-pressed={selected}
                      aria-label={`Filter by ${publisher.name}`}
                      title={publisher.name}
                      onClick={() => selectPublisher(publisher.id)}
                    >
                      <img
                        src={getPublisherAvatarUrl(publisher)}
                        alt=""
                        loading="lazy"
                      />
                    </EducationAnalystButton>
                  );
                })}
              </EducationAnalystFilters>

              <EducationFilterActions>
                <MembersDateFilter
                  onRangeChange={handleDatesSelected}
                  clear={isClearDates}
                />
                {hasActiveFilters && (
                  <EducationClearButton type="button" onClick={clearAllFilters}>
                    <span className="material-icons" aria-hidden="true">
                      close
                    </span>
                    Clear filters
                  </EducationClearButton>
                )}
              </EducationFilterActions>
            </EducationFilterRow>
          </EducationFilterCard>
        </EducationContentInner>
      </EducationFiltersSection>

      <EducationFeedSection>
        <EducationContentInner>
          {loading ? (
            <EducationFeedSkeleton />
          ) : posts.length === 0 ? (
            <EducationEmptyState>
              <p>
                {hasActiveFilters
                  ? "No lessons match your filters."
                  : "There are no education posts to show right now. Check back soon for new content."}
              </p>
              {hasActiveFilters && (
                <EducationClearButton type="button" onClick={clearAllFilters}>
                  <span className="material-icons" aria-hidden="true">
                    close
                  </span>
                  Clear filters
                </EducationClearButton>
              )}
            </EducationEmptyState>
          ) : (
            <>
              <EducationGrid>
                {posts.map((post) => (
                  <EducationLessonCard key={post.id} post={post} />
                ))}
              </EducationGrid>
              {totalPages > 1 && (
                <EducationPaginationWrap>
                  <MembersPagination
                    activePage={activePage}
                    totalPages={totalPages}
                    onChange={handlePageChange}
                  />
                </EducationPaginationWrap>
              )}
            </>
          )}
        </EducationContentInner>
      </EducationFeedSection>

      <Footer onSubscribeClick={goToPricing} hideSubscribeCta={loggedIn} />
    </EducationPage>
  );
};

export default Education;
